import { useCallback, useMemo, useRef, useState } from 'react';
import type { MutateOptions } from '@tanstack/react-query';

import { ActorListEnum } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { ActorUnionInput } from '@e-socna/data-layer';

import { actorForm2ApiMapper } from '../../../../Actor/mappers/actor.form2api.mapper';
import type { ActorForm_Actor } from '../../../../Actor/fragments/actor.fragment';

import { actorListKeyOfList } from '../utils/actor-list.util';
import type { Mutations } from '../types/props.type';

import type {
  ActorListsFreeze,
  PreviewFreezes,
  ProposedActorLists,
} from './useActorListsChangeFreezes.hook';

type CurrentActorLists = Readonly<{
  internalActors?: readonly ActorForm_Actor[] | null;
  externalActors?: readonly ActorForm_Actor[] | null;
}>;

interface Params {
  readonly mutations: Mutations;
  readonly previewFreezes?: PreviewFreezes;
  readonly lists: CurrentActorLists;
}

export interface PendingChange {
  readonly freezes: readonly ActorListsFreeze[];
  readonly proceed: () => void;
  readonly cancel: () => void;
  // The verdicts asked again, a level refusing the change possibly fixed meanwhile.
  readonly recheck: () => Promise<void>;
}

const LIST_LABELS: Readonly<Record<ActorListEnum, string>> = {
  [ActorListEnum.Internal]: 'acteurs internes',
  [ActorListEnum.External]: 'acteurs externes',
};

export const describeFreezes = (freezes: readonly ActorListsFreeze[]) =>
  freezes
    .map(
      ({ level, lists }) =>
        `${level} (${lists.map((list) => LIST_LABELS[list]).join(', ')})`,
    )
    .join(', ');

// The change given up by the user: the form tells it as it tells a refusal of the back.
const CANCELLED_CHANGE = Object.assign(
  new Error("Modification annulée : rien n'a été enregistré."),
  {
    response: {
      errors: [
        {
          message: "Modification annulée : rien n'a été enregistré.",
          extensions: {},
        },
      ],
    },
  },
);

const toInputs = (actors: readonly ActorForm_Actor[] | null | undefined) =>
  actors?.map((actor) => actorForm2ApiMapper(actor)) ?? null;

// A change of the actors of an existing folder or mission freezing the levels inheriting its
// lists (as the back computes it) is confirmed by the user first, then told once made.
export const useFreezeConfirmedMutations = ({
  mutations,
  previewFreezes,
  lists,
}: Readonly<Params>) => {
  const [pendingChange, setPendingChange] = useState<PendingChange>();
  const [frozen, setFrozen] =
    useState<readonly ActorListsFreeze[]>(EMPTY_ARRAY);
  // The verdicts of the pending change as last asked, the ones its confirmation applies.
  const verdictsRef = useRef<readonly ActorListsFreeze[]>(EMPTY_ARRAY);

  const proposedLists = useCallback(
    (
      list: ActorListEnum,
      changeList: (
        actors: readonly ActorUnionInput[],
      ) => readonly ActorUnionInput[] | null,
    ): ProposedActorLists => {
      const changedKey = actorListKeyOfList(list);
      const current = {
        internalActors: toInputs(lists.internalActors),
        externalActors: toInputs(lists.externalActors),
      };

      return {
        ...current,
        [changedKey]: changeList(current[changedKey] ?? EMPTY_ARRAY),
      };
    },
    [lists.externalActors, lists.internalActors],
  );

  const confirmed = useCallback(
    <V,>(
      proposedOf: (variables: V) => ProposedActorLists,
      mutate: (
        variables: V,
        options?: MutateOptions<unknown, Error, V>,
      ) => void,
    ) =>
      (variables: V, options?: MutateOptions<unknown, Error, V>) => {
        if (!previewFreezes) {
          mutate(variables, options);
          return;
        }

        const proposed = proposedOf(variables);

        // A preview the back fails to give lets the change be made: the mutation tells why.
        previewFreezes(proposed).then(
          (freezes) => {
            if (!freezes.length) {
              mutate(variables, options);
              return;
            }

            verdictsRef.current = freezes;

            const recheck = async () => {
              const rechecked = await previewFreezes(proposed);

              verdictsRef.current = rechecked;
              setPendingChange(
                (current) => current && { ...current, freezes: rechecked },
              );
            };

            setPendingChange({
              freezes,
              recheck,
              proceed: () => {
                setFrozen(verdictsRef.current);
                mutate(variables, options);
              },
              // The submit handlers read the error and the variables only: the mutation context
              // of React Query, absent from a mutation never run, is not given.
              cancel: () => {
                options?.onError?.(CANCELLED_CHANGE, variables, undefined, {
                  client: undefined as never,
                  meta: undefined,
                });
              },
            });
          },
          () => {
            mutate(variables, options);
          },
        );
      },
    [previewFreezes],
  );

  const confirmedMutations = useMemo<Mutations>(
    () => ({
      create: confirmed(
        ({ list, actor }) =>
          proposedLists(list, (actors) => [...actors, actor]),
        mutations.create,
      ),
      update: confirmed(
        ({ list, actorId, update }) =>
          proposedLists(list, (actors) =>
            actors.map((actor) => (idOf(actor) === actorId ? update : actor)),
          ),
        mutations.update,
      ),
      remove: confirmed(
        ({ list, actorId }) =>
          proposedLists(list, (actors) =>
            actors.filter((actor) => idOf(actor) !== actorId),
          ),
        mutations.remove,
      ),
      flush:
        mutations.flush &&
        confirmed(
          ({ list }) => proposedLists(list, () => null),
          mutations.flush,
        ),
      customize: mutations.customize,
    }),
    [confirmed, mutations, proposedLists],
  );

  const closeFrozenAlert = useCallback(() => {
    setFrozen(EMPTY_ARRAY);
  }, []);

  return {
    closeFrozenAlert,
    confirmedMutations,
    frozen,
    pendingChange,
    setPendingChange,
  };
};

const idOf = ({ organization, person }: Readonly<ActorUnionInput>) =>
  organization?._id ?? person?._id;
